import { useEffect, type ReactNode } from 'react';
import type { Control, SubmitHandler } from 'react-hook-form';
import { Controller, useController, useForm, useWatch } from 'react-hook-form';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { logger } from '../../../lib/logger.ts';
import type { SalesDraftDefaultHeaderOutput, SalesDraftType } from '../../../server/types/drafts-types.ts';
import { SearchEntity } from '../../../server/types/search-types.ts';
import { useAcl, useAppConfig } from '../../acl/acl-provider.tsx';
import { Can } from '../../acl/can.tsx';
import { useCustomerBasis } from '../../customer-detail/hooks/use-customer-basis.ts';
import { DialogForm } from '../../dialog/dialog-form.tsx';
import { EntitySearchCombobox } from '../../shared/components/form-items-with-fetch/entity-search-combobox.tsx';
import { useSalesDraftDefaults } from '../hooks/use-sales-draft-defaults.ts';
import { SearchStorageLocationCombobox } from './search-storage-location-combobox.tsx';

type FormInputs = {
    customer_id: string;
    storage_id: string;
    billing_address: SalesDraftDefaultHeaderOutput['invoice_address'];
    shipping_address?: SalesDraftDefaultHeaderOutput['shipping_address'];
    shipping_type_id: string;
    delivery_date: string;
    due_date: string;
    external: Record<string, unknown>;
    type: SalesDraftType;
    notes: string;
};

export type DraftOrderFormProps = {
    customerId?: string;
    storageId?: string;
    onSubmit: SubmitHandler<FormInputs>;
    onCancel: () => void;
    onDirtyChange?: (isDirty: boolean) => void;
    isLoading?: boolean;
    isDisabled?: boolean;
    defaultSelectedDraftType?: SalesDraftType;
};

export const DraftOrderForm = ({
    customerId,
    storageId,
    onSubmit,
    onCancel,
    onDirtyChange,
    isLoading,
    isDisabled,
    defaultSelectedDraftType,
}: DraftOrderFormProps): ReactNode => {
    const { t } = useLocale();
    const { transactionDraftTypes: availableTypes } = useAppConfig();

    // `availableTypes` undefined means no restriction (all types allowed); otherwise the
    // default must be a member, else we fall back to the first allowed type.
    const isDefaultAllowed =
        defaultSelectedDraftType && (!availableTypes || availableTypes.includes(defaultSelectedDraftType));
    const orderType = isDefaultAllowed ? defaultSelectedDraftType : (availableTypes?.[0] ?? 'ORDER');

    const { control, formState, handleSubmit, watch, reset, trigger } = useForm<FormInputs>({
        defaultValues: {
            customer_id: customerId ?? '',
            storage_id: storageId ?? '',
            type: orderType,
            billing_address: {},
            shipping_address: {},
            shipping_type_id: '',
            delivery_date: '',
            due_date: '',
            external: {},
            notes: '',
        },
        mode: 'all',
    });

    useEffect(() => {
        if (!isDisabled) {
            onDirtyChange?.(formState.isDirty);
        }
    }, [formState.isDirty, onDirtyChange, isDisabled]);

    const watchCustomerId = watch('customer_id');

    const customerBasis = useCustomerBasis({
        customerId,
    });

    const {
        data: defaults,
        isLoading: isLoadingDefault,
        isError: isErrorDefault,
    } = useSalesDraftDefaults({
        customerId: watchCustomerId,
        orderType,
    });

    const showLoading = formState.isSubmitting || isLoadingDefault || isLoading;

    useEffect(() => {
        logger.debug('defaults', defaults);

        if (!defaults) {
            return;
        }

        // Reset so server defaults become the new baseline (otherwise per-field setValue
        // leaves _formValues diverged from _defaultValues and isDirty flips on load).
        // customer_id stays at the prop so a user-picked customer remains dirty against it.
        reset(
            {
                customer_id: customerId ?? '',
                storage_id: defaults.storage_id,
                type: orderType,
                billing_address: defaults.invoice_address,
                // empty shipping address means "use billing address" — keep it as {} so
                // the field stays defined and isDirty compares against a stable baseline.
                shipping_address: Object.values(defaults.shipping_address).some(Boolean)
                    ? defaults.shipping_address
                    : {},
                shipping_type_id: defaults.shipping_type,
                delivery_date: defaults.desired_delivery_date,
                due_date: defaults.due_date,
                external: { document_type: defaults.sales_order_type },
                notes: '',
            },
            { keepDirtyValues: true },
        );
        // reset() with keepDirtyValues doesn't re-run validation, so isValid can stay
        // stale at false (storage_id was empty before defaults arrived) and the submit
        // button remains disabled. Trigger explicitly so isValid reflects the new values.
        void trigger();
    }, [defaults, reset, trigger, customerId, orderType]);

    return (
        <>
            <DialogForm.Content>
                <DialogForm.ItemList col>
                    <DialogForm.Item>
                        <DialogForm.Label>{t('sales_draft__new_form___customer')}</DialogForm.Label>
                        <DialogForm.Body>
                            <Controller
                                name="customer_id"
                                control={control}
                                rules={{
                                    required: true,
                                }}
                                render={({ field: { value, onChange } }) => {
                                    return (
                                        <EntitySearchCombobox
                                            className="w-full"
                                            entities={[SearchEntity.Customer]}
                                            disabled={isDisabled}
                                            tracking={{
                                                context: 'draft_customer_select',
                                                context_entity_type: 'draft',
                                            }}
                                            defaultValue={
                                                value && customerBasis.data?.customerName
                                                    ? [
                                                          {
                                                              id: value,
                                                              name: customerBasis.data.customerName,
                                                              entity: SearchEntity.Customer,
                                                          },
                                                      ]
                                                    : undefined
                                            }
                                            inputPlaceholder={t('placeholder__select_customer')}
                                            onValueChange={(value) => {
                                                const [item] = value;
                                                onChange(item?.id ?? '');
                                            }}
                                        />
                                    );
                                }}
                            />
                        </DialogForm.Body>
                    </DialogForm.Item>
                    {/**
                     * OK here it is tricky, we need to check if the user has the permission to read the storage_id field
                     * because eBootis does not use this field to submit the sales draft
                     */}
                    <Can I="read" a="SalesDraft" field="storage_id">
                        <DialogForm.Item>
                            <DialogForm.Label>
                                {t('sales_draft__new_form___storage_location')}
                            </DialogForm.Label>
                            <DialogForm.Body>
                                <SearchLocationByCustomer
                                    control={control}
                                    isDisabled={isDisabled}
                                    isLoadingDefault={isLoadingDefault}
                                />
                            </DialogForm.Body>
                        </DialogForm.Item>
                    </Can>

                    {isErrorDefault && <DialogForm.Error error={t('writeback_error')} />}
                </DialogForm.ItemList>
            </DialogForm.Content>
            <DialogForm.SubmitBlock
                disabled={!formState.isValid || showLoading || isErrorDefault || isDisabled}
                isLoading={showLoading}
                onCancel={onCancel}
                onSubmit={handleSubmit(onSubmit)}
            />
        </>
    );
};

const SearchLocationByCustomer = ({
    control,
    isLoadingDefault,
    isDisabled,
}: {
    control: Control<FormInputs>;
    isLoadingDefault: boolean;
    isDisabled?: boolean;
}): ReactNode => {
    const acl = useAcl();

    const customerId = useWatch({
        control,
        name: 'customer_id',
        // defaultValue: 'default', // default value before the render
    });

    const { field } = useController({
        name: 'storage_id',
        control,
        rules: {
            required: true,
        },
    });

    // TODO: move this to sales-draft feature folder
    return (
        <SearchStorageLocationCombobox
            customerId={customerId}
            value={field.value}
            onChange={field.onChange}
            disabled={isDisabled || !acl.can('update', 'SalesDraft', 'storage_id')}
            isLoading={isLoadingDefault}
        />
    );
};
